import { NgClass, NgStyle } from '@angular/common';
import { Component, effect, inject, input, signal } from '@angular/core';
import { DXCAuthService } from '@dxc/data-access';
import { environment } from '@dxc/environments';

/**
 * AvatarComponent is responsible for displaying user avatars.
 * It supports two sizes and can display a default avatar if the user's avatar hasn't loaded.
 * It utilizes Angular's common directives for conditional styling and displaying content.
 * It can accept an optional avatar URL to use instead of the user's avatar.
 */
@Component({
  selector: 'dxc-avatar',
  imports: [NgClass, NgStyle],
  template: `
    <!-- Avatar container with conditional classes for size and styles for background image -->
    <div
      class="border-border bg-border flex items-center justify-center rounded-full border bg-cover font-semibold"
      [ngClass]="{
        'h-[34px] w-[34px] text-sm': size() === 'sm',
        'h-[60px] w-[60px] text-sm': size() === 'lg',
        'h-[120px] w-[120px] text-3xl': size() === 'xl',
        'h-[38px] w-[38px] text-sm': size() === 'default'
      }"
      [ngStyle]="{
        'background-image': showImage()
          ? 'url(' + currentAvatarUrl() + ')'
          : 'none'
      }"
    >
      <!-- Display user initials if avatar hasn't loaded -->
      <!-- User initials are dynamically calculated based on the user's name -->
      @if (!showImage()) {
        {{ getUserInitials() }}
      }
    </div>
    <!-- Hidden img tag for preloading the avatar and triggering load event -->
    <img
      style="display: none;"
      [src]="currentAvatarUrl()"
      (error)="onImageError()"
      (load)="onImageLoad()"
    />
  `
})
export class AvatarComponent {
  public size = input<'sm' | 'lg' | 'xl' | 'default'>('default');
  public avatarUrl = input<string | undefined>(undefined);
  public useInitialsAsFallback = input<boolean>(false);
  public userName = input<string | undefined>(undefined);
  protected dxcAuthService = inject(DXCAuthService);
  protected user = this.dxcAuthService.userSignal();
  protected avatarLoaded = signal(false);
  protected currentAvatarUrl = signal('');
  protected defaultAvatar = 'images/default-avatar.png';
  protected showImage = signal(true);

  constructor() {
    effect(() => this.updateAvatarUrl());
  }

  protected updateAvatarUrl(): void {
    const avatarUrl = this.avatarUrl();
    if (avatarUrl) {
      this.currentAvatarUrl.set(`${environment.apiUrl}/${avatarUrl}`);
    } else {
      this.currentAvatarUrl.set(this.user?.profile_pic || this.defaultAvatar);
    }
    this.showImage.set(true);
    this.avatarLoaded.set(false);
  }

  /**
   * Calculates and returns the user's initials.
   * If the user has a first and last name, it uses the first letter of each.
   * If only one name is present, it uses the first two letters of that name.
   * Returns an empty string if no name is available.
   */
  protected getUserInitials(): string {
    const name = this.userName() || this.user?.name || '';
    const names = name.split(' ');
    return names.length > 1
      ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
      : name
        ? name.substring(0, 2).toUpperCase()
        : '';
  }

  /**
   * Event handler for the image load event.
   * Sets the avatarLoaded flag to true when the avatar image has loaded.
   */
  protected onImageLoad() {
    this.avatarLoaded.set(true);
    this.showImage.set(true);
  }

  /**
   * Event handler for the image error event.
   * Sets the avatarLoaded flag to false and falls back to the profile picture or default avatar.
   */
  protected onImageError() {
    this.avatarLoaded.set(false);
    this.showImage.set(!this.useInitialsAsFallback());
    if (!this.useInitialsAsFallback()) {
      this.currentAvatarUrl.set(this.defaultAvatar);
    }
  }
}
